import React, {
  FC,
  useEffect,
  useState,
  useRef,
  IframeHTMLAttributes,
  SetStateAction,
} from "react";
import { Accordion, Card, Tab } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  Checkbox,
  DefaultButton,
  Panel,
  PanelType,
  Pivot,
  PivotLinkSize,
  PivotLinkFormat,
  PivotItem,
  IPivotStyles,
  IStyleSet,
  PrimaryButton,
  Stack,
  Separator,
  Toggle,
  mergeStyleSets,
  Icon,
  Image,
  FontIcon,
} from "@fluentui/react";
import Moment from "react-moment";
import { DailyReport } from "../../models";
import DepartureButton from "./DepartureButton";
import ReportDetails from "./ReportDetails";
import PrintPage from "./PrintPage";
import ReactToPrint from "react-to-print";
import "./Reports.css";
import { Get } from "../../api/apiUtils";
import SofIcon from "../common/Icons";
import {
  mergedPdfs,
  mergedPdfsAsBytes,
} from "../../services/PdfDocumentService";

type Props = {
  dailyReport: DailyReport;
  setSelectedFileUrl: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFileName: React.Dispatch<React.SetStateAction<string>>;
  setPdfContentBlob: React.Dispatch<React.SetStateAction<Blob | undefined>>;
};

const classes = mergeStyleSets({
  viewButton: {
    width: "39px",
    height: "39px",
    borderRadius: "5px",
    border: "1px solid #E2E6E8",
    backgroundColor: "#FAFAFA",
    display: "block",
    padding: "7px 11px",
    marginRight: "10px",
    cursor: "pointer",
    selectors: {
      ":hover": {
        backgroundColor: "#EEEEEE",
        textDecoration: "none",
      },
    },
  },
  ovalInProgress: {
    width: "120px",
    height: "25px",
    background: "rgba(24, 144, 255, 0.1)",
    borderRadius: "50px",
    fontSize: "small",
    padding: "2px 5px",
    color: "#1890FF",
  },
  ovalDeparted: {
    width: "120px",
    height: "25px",
    background: "rgba(76, 175, 80, 0.1)",
    borderRadius: "50px",
    fontSize: "small",
    padding: "2px 5px",
    color: "#4CAF50",
  },
  ovalRejected: {
    width: "120px",
    height: "25px",
    background: "rgba(211, 47, 47, 0.2)",
    borderRadius: "50px",
    fontSize: "small",
    padding: "2px 5px",
    color: "#D32F2F",
  },
  inlineBlockDiv: {
    display: "inline-block",
    paddingRight: "10px",
    width: "40px",
  },
  fileContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5em",
  },
  fileCard: {
    padding: 20,
    border: "0.5px solid #009FDF",
    height: 100,
  },
});

const pivotStyles: Partial<IStyleSet<IPivotStyles>> = {
  link: {
    minWidth: "150px",
    selectors: {
      ":hover": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
      ":active": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
    },
  },
  linkIsSelected: {
    minWidth: "150px",
    backgroundColor: "#EEF0F8",
    color: "#323130",
    selectors: {
      ":hover": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
      ":active": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
    },
  },
};

const ReportItem: FC<Props> = (props: Props) => {
  const [selectedDailyReport, setSelectedDailyReport] = useState<DailyReport>();
  const [panelOpened, setPanelOpened] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedLDLCount, setSelectedLDLCount] = useState(0);
  const [selectedServicesCount, setSelectedServicesCount] = useState(0);
  const [includeComment, setIncludeComment] = useState(true);
  const [printSelected, setPrintSelected] = useState(false);
  const [auditLogSelected, setAuditLogSelected] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedReport, setSelectedReport] = useState("Summary");
  const [isPrinting, setIsPrinting] = useState(false);
  const [files, setFiles] = useState<any[]>([]);

  const openPanel = (
    isOpen: boolean,
    isPrint: boolean,
    isAuditLog: boolean,
    dailyReport: DailyReport | undefined
  ) => {
    setPrintSelected(isPrint);
    setAuditLogSelected(isAuditLog);

    if (dailyReport !== undefined) {
      setSelectedDailyReport(dailyReport);
    }

    setPanelOpened(isOpen);
  };

  const closePanel = React.useCallback((ev?: React.SyntheticEvent) => {
    if (ev) {
      ev.preventDefault();
      setPanelOpened(false);
    }
  }, []);

  const onRenderPrintFooterContent = React.useCallback(
    () => (
      <div>
        <ReactToPrint
          trigger={() => (
            <PrimaryButton className="btn-prim">Print Report</PrimaryButton>
          )}
          content={() => componentRef.current}
        />
        &nbsp;
        <DefaultButton
          className="btn-def"
          onClick={() => {
            openPanel(false, false, false, undefined);
          }}
        >
          Cancel
        </DefaultButton>
      </div>
    ),
    []
  );

  const onRenderAuditLogFooterContent = React.useCallback(
    () => (
      <div>
        <DefaultButton
          className="btn-def"
          onClick={() => {
            openPanel(false, false, false, undefined);
          }}
        >
          Close
        </DefaultButton>
      </div>
    ),
    []
  );

  const onRenderServicesPrintFooterContent = React.useCallback(
    () => (
      <div>
        <PrimaryButton
          className="btn-prim"
          onClick={onPrintServices}
          disabled={isPrinting}
        >
          Print Report
        </PrimaryButton>
        &nbsp;
        <DefaultButton
          className="btn-def"
          onClick={() => {
            openPanel(false, false, false, undefined);
          }}
        >
          Cancel
        </DefaultButton>
      </div>
    ),
    [files, isPrinting]
  );

  const onPrintServices = async () => {
    setIsPrinting(true);
    let pdfs: any[] = [];

    for (const file of files.filter((f) => f.isSelected)) {
      const existingPdfBytes = await fetch(file.url).then((res) =>
        res.arrayBuffer()
      );
      pdfs.push(existingPdfBytes);
    }

    const contents = await mergedPdfsAsBytes(pdfs);
    const blob = new Blob([contents], { type: "application/pdf" });
    const mergedPdfs = URL.createObjectURL(blob);
    console.log(mergedPdfs);
    const iframe = document.createElement("iframe") as HTMLIFrameElement;
    document.body.appendChild(iframe);
    iframe.style.display = "none";
    iframe.src = mergedPdfs;

    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow?.print();
        setIsPrinting(false);
      }, 500);
    };
  };

  const selectAll = (checked?: boolean) => {
    if (selectedDailyReport && checked !== undefined) {
      selectedDailyReport.nominations.forEach((nomination) => {
        nomination.isSelected = checked;
      });

      if (checked) {
        setSelectedCount(selectedDailyReport.nominations.length);
      } else {
        setSelectedCount(0);
      }
    }
  };

  const selectAllLDL = (checked?: boolean) => {
    if (selectedDailyReport && checked !== undefined) {
      selectedDailyReport.longDockLogReport.nominations.forEach(
        (nomination) => {
          nomination.isSelected = checked;
        }
      );

      if (checked) {
        setSelectedLDLCount(selectedDailyReport.nominations.length);
      } else {
        setSelectedLDLCount(0);
      }
    }
  };

  const selectNomination = (nominationNo: string, checked?: boolean) => {
    if (selectedDailyReport && checked !== undefined) {
      selectedDailyReport.nominations.forEach((nomination) => {
        if (nomination.nominationNo === nominationNo) {
          nomination.isSelected = checked;
        }
      });

      setSelectedCount(
        selectedDailyReport.nominations.filter((n) => n.isSelected).length
      );
    }
  };

  const selectLDLNomination = (nominationNo: string, checked?: boolean) => {
    if (selectedDailyReport && checked !== undefined) {
      selectedDailyReport.longDockLogReport.nominations.forEach(
        (nomination) => {
          if (nomination.nominationNo === nominationNo) {
            nomination.isSelected = checked;
          }
        }
      );

      setSelectedLDLCount(
        selectedDailyReport.longDockLogReport.nominations.filter(
          (n) => n.isSelected
        ).length
      );
    }
  };

  const selectService = (attachmentId: number, checked?: boolean) => {
    const updated = files.map((item) =>
      item.attachmentId === attachmentId
        ? { ...item, isSelected: checked }
        : item
    );
    setFiles(updated);
    setSelectedServicesCount(updated.filter((n) => n.isSelected).length);
  };

  const selectAllServices = (checked?: boolean) => {
    const updated = files.map((item) => {
      return { ...item, isSelected: checked };
    });
    setFiles(updated);
    setSelectedServicesCount(updated.filter((n) => n.isSelected).length);
  };

  const formatBytes = (bytes: number, decimals: number) => {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };
  const loadFiles = async () => {
    const response = await Get(
      `/api/Attachment/marine/reports/${props.dailyReport.lineHandlingNo}`
    );
    const resultRes = await response.json();
    const attachments = await Promise.all(
      (resultRes || []).map(async (file: any) => {
        //const base64Data = await getBase64FromUrl(file.url);
        return {
          attachmentId: file.attachmentId,
          //previewBase64: base64Data,
          url: file.url,
          name: file.fileName,
          size: file.fileSize,
          isSelected: false,
        };
      })
    );
    return attachments;
  };

  useEffect(() => {
    const handleFiles = async () => {
      const data = await loadFiles();
      setFiles(data ?? []);
    };
    handleFiles();
  }, []);

  const componentRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Accordion>
        <Card style={{ border: "none" }}>
          <Card.Header
            style={{
              border: "none",
              backgroundColor: "#FFFFFF",
              padding: "0",
            }}
          >
            <Accordion.Toggle
              as="div"
              variant="div"
              eventKey={props.dailyReport.lineHandlingNo}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <div
                className="ms-Grid"
                dir="ltr"
                style={{ backgroundColor: "white" }}
              >
                <div
                  className="ms-Grid-row"
                  style={{
                    padding: "10px 10px 10px 5px",
                  }}
                >
                  <div className="ms-Grid-col ms-md3">
                    <div
                      className={`${props.dailyReport.vehicleType}-icon`}
                    ></div>
                    <span className="list-item-title">
                      {props.dailyReport.vehicleName}
                    </span>
                  </div>
                  <div className="ms-Grid-col ms-md2">
                    <Link
                      to={`/marine/${props.dailyReport.lineHandlingNo}/sof`}
                    >
                      {props.dailyReport.lineHandlingNo.substr(
                        props.dailyReport.lineHandlingNo.length - 5
                      )}
                    </Link>
                  </div>
                  <div className="ms-Grid-col ms-md2">
                    <DepartureButton
                      dailyReport={props.dailyReport}
                      onSuccess={(value: string) => {
                        props.dailyReport.departureDate = value;
                      }}
                    />
                  </div>
                  <div
                    className="ms-Grid-col ms-md2"
                    style={{ paddingLeft: "40px" }}
                  >
                    {props.dailyReport.nominationCount}
                  </div>
                  <div className="ms-Grid-col ms-md2">
                    {props.dailyReport.lineHandlingStatus === "Departed" && (
                      <span className={classes.ovalDeparted}>
                        {props.dailyReport.lineHandlingStatus}
                      </span>
                    )}

                    {props.dailyReport.lineHandlingStatus === "Rejected" && (
                      <span className={classes.ovalRejected}>
                        {props.dailyReport.lineHandlingStatus}
                      </span>
                    )}
                    {props.dailyReport.lineHandlingStatus === "In Progress" && (
                      <span className={classes.ovalInProgress}>
                        {props.dailyReport.lineHandlingStatus}
                      </span>
                    )}
                  </div>
                  <div
                    className="ms-Grid-col ms-md1"
                    style={{
                      display: "flex",
                      flexDirection: "row-reverse",
                    }}
                  >
                    <div className={classes.viewButton}>
                      <Icon
                        iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                      />
                    </div>
                    <div
                      className={classes.viewButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPanel(true, true, false, props.dailyReport);
                      }}
                    >
                      <Icon iconName="Print" />
                    </div>
                    <div
                      className={classes.viewButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPanel(true, false, true, props.dailyReport);
                      }}
                    >
                      <Icon iconName="DocumentSearch" />
                    </div>
                  </div>
                </div>
              </div>
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey={`${props.dailyReport.lineHandlingNo}`}>
            <div className="table-container">
              <ReportDetails
                dailyReport={props.dailyReport}
                key={props.dailyReport.lineHandlingNo}
                setSelectedFileUrl={props.setSelectedFileUrl}
                setSelectedFileName={props.setSelectedFileName}
                setPdfContentBlob={props.setPdfContentBlob}
              />
            </div>
          </Accordion.Collapse>
        </Card>
      </Accordion>

      <Panel
        isLightDismiss
        isOpen={panelOpened}
        onDismissed={closePanel}
        closeButtonAriaLabel="Close"
        type={PanelType.custom}
        customWidth={"650px"}
        isFooterAtBottom={true}
        headerText={props.dailyReport.vehicleName}
        onRenderFooterContent={
          printSelected
            ? selectedReport === "Services"
              ? onRenderServicesPrintFooterContent
              : onRenderPrintFooterContent
            : onRenderAuditLogFooterContent
        }
        styles={{
          headerText: { color: "#12427d" },
        }}
      >
        <div>
          {"Line Handling# "}
          <strong>
            {`${props.dailyReport.lineHandlingNo.substr(
              props.dailyReport.lineHandlingNo.length - 5
            )}`}
          </strong>
        </div>
        <Separator></Separator>
        {printSelected && (
          <Pivot
            linkFormat={"tabs"}
            styles={pivotStyles}
            linkSize={"normal"}
            onLinkClick={(item?: PivotItem | undefined) =>
              item &&
              item.props.headerText &&
              setSelectedReport(item.props.headerText)
            }
          >
            <PivotItem key="Summary" itemKey="Summary" headerText="Summary">
              <div
                style={{
                  marginTop: "20px",
                }}
              >
                <span>
                  <strong>Select Line #</strong>
                  <br />
                  <span style={{ fontSize: "12px" }}>
                    {`${selectedLDLCount} out of ${props.dailyReport.nominations.length} selected`}
                  </span>
                </span>
                <span style={{ float: "right", marginTop: "-10px" }}>
                  <Checkbox
                    label="Select All"
                    checked={
                      selectedLDLCount === props.dailyReport.nominations.length
                    }
                    onChange={(e, value?: boolean) => selectAllLDL(value)}
                  />
                </span>
              </div>
              {props.dailyReport.longDockLogReport.nominations.map(
                (item, index: number) => (
                  <div
                    style={{
                      marginTop: "10px",
                      border: "0.5px solid #009FDF",
                      padding: "10px",
                    }}
                  >
                    <span>
                      <strong>{`Line# ${item.ourLineNumber}`}</strong>
                      <br />
                      <span>{item.customer}</span>
                      <br />

                      <span>
                        <Stack tokens={{ childrenGap: 8 }} horizontal>
                          <span
                            style={{
                              borderRight: "0.5px solid",
                              paddingRight: "10px",
                            }}
                          >
                            Tank: <strong>{item.tank}</strong>
                          </span>
                          <span
                            style={{
                              borderRight: "0.5px solid",
                              paddingRight: "10px",
                            }}
                          >
                            Qty: <strong>{item.quantity}</strong>
                          </span>
                          <div style={{ width: "300px" }}>
                            Product: <strong>{item.product}</strong>
                          </div>
                        </Stack>
                      </span>
                    </span>
                    <span style={{ float: "right", marginTop: "-60px" }}>
                      <Checkbox
                        checked={item.isSelected}
                        onChange={(e, value?: boolean) =>
                          selectLDLNomination(item.nominationNo, value)
                        }
                      />
                    </span>
                  </div>
                )
              )}
            </PivotItem>
            <PivotItem key="Details" itemKey="Details" headerText="Details">
              <div
                style={{
                  marginTop: "20px",
                  backgroundColor: "#F2F4F6",
                  padding: "15px 10px",
                }}
              >
                <span>Include comments per nomination</span>
                <span style={{ float: "right" }}>
                  <Toggle
                    checked={includeComment}
                    onText="Yes"
                    offText="No"
                    onChange={(e, value?: boolean) =>
                      setIncludeComment(value === undefined ? false : value)
                    }
                  />
                </span>
              </div>
              <div
                style={{
                  marginTop: "20px",
                }}
              >
                <span>
                  <strong>Select Nomination</strong>
                  <br />
                  <span style={{ fontSize: "12px" }}>
                    {`${selectedCount} out of ${props.dailyReport.nominations.length} selected`}
                  </span>
                </span>
                <span style={{ float: "right", marginTop: "-10px" }}>
                  <Checkbox
                    label="Select All"
                    checked={
                      selectedCount === props.dailyReport.nominations.length
                    }
                    onChange={(e, value?: boolean) => selectAll(value)}
                  />
                </span>
              </div>
              {props.dailyReport.nominations.map((item, index: number) => (
                <div
                  style={{
                    marginTop: "10px",
                    border: "0.5px solid #009FDF",
                    padding: "10px",
                  }}
                >
                  <span>
                    <strong>
                      {`Nomination# ${item.nominationNo.substr(
                        item.nominationNo.length - 5
                      )}`}
                    </strong>
                    <br />
                    <span>{item.product}</span>
                    <br />

                    <span>
                      <Stack tokens={{ childrenGap: 8 }} horizontal>
                        <span
                          style={{
                            borderRight: "0.5px solid",
                            paddingRight: "10px",
                          }}
                        >
                          Tank: <strong>{item.tank}</strong>
                        </span>
                        <span
                          style={{
                            borderRight: "0.5px solid",
                            paddingRight: "10px",
                          }}
                        >
                          Qty: <strong>{item.quantity}</strong>
                        </span>
                        <div style={{ width: "300px" }}>
                          Product: <strong>{item.product}</strong>
                        </div>
                      </Stack>
                    </span>
                  </span>
                  <span style={{ float: "right", marginTop: "-60px" }}>
                    <Checkbox
                      checked={item.isSelected}
                      onChange={(e, value?: boolean) =>
                        selectNomination(item.nominationNo, value)
                      }
                    />
                  </span>
                </div>
              ))}
            </PivotItem>
            <PivotItem key="Services" itemKey="Services" headerText="Services">
              <div style={{ paddingTop: 20, paddingBottom: 20 }}>
                <span>
                  <strong>Select Services</strong>
                  <br />
                  <span style={{ fontSize: "12px" }}>
                    {`${selectedServicesCount} out of ${files.length} selected`}
                  </span>
                </span>
                <span style={{ float: "right", marginTop: "-10px" }}>
                  <Checkbox
                    label="Select All"
                    checked={selectedServicesCount === files.length}
                    onChange={(e, value?: boolean) => selectAllServices(value)}
                  />
                </span>
              </div>
              <div className={classes.fileContainer}>
                {files?.length > 0 ? (
                  files?.map(
                    (
                      item: {
                        attachmentId: number;
                        name: string;
                        previewBlob: string;
                        isSelected: boolean;
                        size: number;
                      },
                      index: number
                    ) => {
                      return (
                        <div
                          className={classes.fileCard}
                          key={`grid-card-${index}`}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <SofIcon iconname="PDFFile" />
                              <br />
                              <span style={{ fontSize: "12px", opacity: 0.5 }}>
                                {formatBytes(item.size, 2)}
                              </span>
                            </div>
                            <div style={{ paddingLeft: 20, marginTop: -15 }}>
                              {item.name}
                            </div>
                          </div>
                          <span style={{ float: "right", marginTop: "-70px" }}>
                            <Checkbox
                              checked={item.isSelected}
                              onChange={(e, value?: boolean) =>
                                selectService(item.attachmentId, value)
                              }
                            />
                          </span>
                        </div>
                      );
                    }
                  )
                ) : (
                  <></>
                )}
              </div>
            </PivotItem>
          </Pivot>
        )}
        {!printSelected && (
          <div>
            {props.dailyReport.lineHandlingLogs.map((item, index: number) => (
              <div
                style={{
                  marginTop: "10px",
                  border: "0.5px solid #009FDF",
                  padding: "10px",
                }}
              >
                <div style={{ marginBottom: "10px" }}>
                  <strong>{item.status}</strong>
                  <div style={{ float: "right" }}>
                    {" "}
                    <FontIcon iconName="clock" />
                    &nbsp;
                    <Moment format="HH:mm - ll" local>
                      {`${item.updatedOn} UTC`}
                    </Moment>{" "}
                  </div>
                </div>
                <table>
                  <tr>
                    <td>
                      <span className={classes.inlineBlockDiv}>
                        {item.profilePicture && (
                          <Image
                            style={{
                              borderRadius: "50%",
                              height: "auto",
                            }}
                            src={`data:image/jpeg;base64, ${item.profilePicture}`}
                            width="30"
                            height="30"
                          />
                        )}
                      </span>
                    </td>
                    <td>
                      <b>{item.updatedBy}</b>
                    </td>
                  </tr>
                  {item.remarks && (
                    <tr>
                      <td></td>
                      <td style={{ whiteSpace: "pre-wrap" }}>{item.remarks}</td>
                    </tr>
                  )}
                </table>
              </div>
            ))}
          </div>
        )}
      </Panel>
      <div style={{ display: "none" }}>
        <div ref={componentRef}>
          <PrintPage
            dailyReport={props.dailyReport}
            reportType={selectedReport}
            includeComments={includeComment}
            key={props.dailyReport.lineHandlingNo}
          />
        </div>
      </div>
    </>
  );
};

export default ReportItem;
